// React, Next
import { useContext, ReactNode } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Styles
import themeColors from '@/assets/theme/colors'
import breakpoints from '@/assets/theme/breakpoints'
import classes from './PrivacyView-CSS'

export default function PrivacyView() {
  const { t } = useTranslation('privacy')
  const { screenDimensions } = useContext(GlobalContext)

  const wide = screenDimensions.width > breakpoints.mobileSmall
  const bodySize = wide ? '0.93rem' : '0.85rem'

  const h2 = (text: string) => (
    <Typography
      variant="h2"
      component="h2"
      sx={classes.h2}
      style={{ fontWeight: 600, fontSize: wide ? '1.3rem' : '1.1rem' }}
    >
      {text}
    </Typography>
  )

  const h3 = (text: string) => (
    <Typography
      variant="h3"
      component="h3"
      sx={classes.h3}
      style={{ fontSize: wide ? '1.1rem' : '0.95rem' }}
    >
      {text}
    </Typography>
  )

  const p = (content: ReactNode, key?: string) => (
    <Typography
      key={key ?? String(content)}
      component="p"
      sx={classes.b2}
      style={{ fontSize: bodySize }}
    >
      {content}
    </Typography>
  )

  const link = (href: string) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: themeColors.info.main, wordBreak: 'break-word' }}
    >
      {href}
    </a>
  )

  /** "Purpose: …" style line, where the label carries the weight. */
  const labelled = (label: string, text: string, tail?: ReactNode) => (
    <Typography
      key={label + text}
      component="p"
      sx={classes.b2}
      style={{ fontSize: bodySize }}
    >
      <Box component="span" sx={{ fontWeight: 700 }}>
        {label}
      </Box>{' '}
      {text}
      {tail}
    </Typography>
  )

  const addressBlock = (lines: string[]) => (
    <Box sx={{ marginBottom: '0.75rem' }}>
      {lines.map(line => (
        <Typography
          key={line}
          component="p"
          sx={{ color: themeColors.gray.mid }}
          style={{ fontSize: bodySize }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  )

  const cookieTable = () => {
    const head = [
      t('labels.cookieName'),
      t('labels.cookiePurpose'),
      t('labels.cookieDuration'),
      t('labels.cookieProvider')
    ]
    const row = [
      t('sections.cookies.row.name'),
      t('sections.cookies.row.purpose'),
      t('sections.cookies.row.duration'),
      t('sections.cookies.row.provider')
    ]

    return (
      // Scrolls inside its own box so the page never scrolls sideways.
      <Box sx={{ overflowX: 'auto', margin: '1rem 0 1.25rem' }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            minWidth: '34rem',
            width: '100%',
            fontSize: bodySize,
            color: themeColors.gray.mid
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {head.map(cell => (
                <Box
                  key={cell}
                  component="th"
                  sx={{
                    textAlign: 'left',
                    padding: '0.6rem 0.75rem',
                    borderBottom: `2px solid ${themeColors.gray.lighter}`,
                    color: themeColors.gray.dark,
                    fontWeight: 700
                  }}
                >
                  {cell}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            <Box component="tr">
              {row.map(cell => (
                <Box
                  key={cell}
                  component="td"
                  sx={{
                    padding: '0.6rem 0.75rem',
                    borderBottom: `1px solid ${themeColors.gray.lighter}`,
                    verticalAlign: 'top'
                  }}
                >
                  {cell}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  const recipient = (key: '01' | '02' | '03', withLink: boolean) => (
    <Box key={key} sx={{ marginBottom: '1rem' }}>
      <Typography
        component="p"
        sx={{ ...classes.b2, fontWeight: 700, color: themeColors.gray.dark }}
        style={{ fontSize: bodySize }}
      >
        {t(`sections.recipients.items.${key}.title` as never)}
      </Typography>
      <Typography component="p" sx={classes.b2} style={{ fontSize: bodySize }}>
        {t(`sections.recipients.items.${key}.body` as never)}
        {withLink && <> {link(t('sections.recipients.items.01.link'))}</>}
      </Typography>
    </Box>
  )

  return (
    <Box sx={classes.mainDiv}>
      <Box
        sx={classes.sndDiv}
        style={{ minHeight: screenDimensions.height - 220 }}
      >
        <Typography
          variant="h1"
          sx={classes.h1}
          style={{ fontSize: wide ? '2rem' : '1.5rem' }}
        >
          {t('h1')}
        </Typography>

        <Typography component="p" sx={classes.b1}>
          {t('version')}
        </Typography>
        <Typography component="p" sx={classes.b1}>
          {t('update')}
        </Typography>

        {/* Which language version actually governs. */}
        <Typography
          component="p"
          sx={{
            ...classes.b1,
            marginTop: '1.25rem',
            marginBottom: '1.5rem',
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            fontStyle: 'italic'
          }}
        >
          {t('bindingNote')}
        </Typography>

        <Box component="section">
          {h2(t('sections.scope.title'))}
          {p(t('sections.scope.p1'))}
          {p(t('sections.scope.p2'))}
        </Box>

        <Box component="section">
          {h2(t('sections.controller.title'))}
          {p(t('sections.controller.p1'))}
          {addressBlock([
            t('sections.controller.company'),
            t('sections.controller.street'),
            t('sections.controller.city'),
            t('sections.controller.country'),
            t('sections.controller.email')
          ])}
        </Box>

        <Box component="section">
          {h2(t('sections.law.title'))}
          {p(t('sections.law.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.collection.title'))}

          {h3(t('sections.collection.contactForm.title'))}
          {p(t('sections.collection.contactForm.p1'))}
          {p(t('sections.collection.contactForm.p2'))}
          {labelled(
            t('labels.purpose'),
            t('sections.collection.contactForm.purpose')
          )}
          {labelled(
            t('labels.legalBasis'),
            t('sections.collection.contactForm.legalBasis')
          )}
          {labelled(
            t('labels.retention'),
            t('sections.collection.contactForm.retention')
          )}
          {p(t('sections.collection.contactForm.p3'))}
          {p(t('sections.collection.contactForm.p4'))}

          {h3(t('sections.collection.hosting.title'))}
          {p(t('sections.collection.hosting.p1'))}
          {labelled(
            t('labels.purpose'),
            t('sections.collection.hosting.purpose')
          )}
          {labelled(
            t('labels.legalBasis'),
            t('sections.collection.hosting.legalBasis')
          )}
          {labelled(
            t('labels.retention'),
            t('sections.collection.hosting.retention'),
            <> {link(t('sections.collection.hosting.retentionLink'))}</>
          )}

          {h3(t('sections.collection.formInfrastructure.title'))}
          {p(t('sections.collection.formInfrastructure.p1'))}
          {labelled(
            t('labels.purpose'),
            t('sections.collection.formInfrastructure.purpose')
          )}
          {labelled(
            t('labels.legalBasis'),
            t('sections.collection.formInfrastructure.legalBasis')
          )}
          {labelled(
            t('labels.retention'),
            t('sections.collection.formInfrastructure.retention')
          )}

          {h3(t('sections.collection.noAnalytics.title'))}
          {p(t('sections.collection.noAnalytics.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.cookies.title'))}
          {p(t('sections.cookies.p1'))}
          {p(t('sections.cookies.p2'))}
          {cookieTable()}
          {p(t('sections.cookies.p3'))}
          {p(t('sections.cookies.p4'))}
        </Box>

        <Box component="section">
          {h2(t('sections.recipients.title'))}
          {p(t('sections.recipients.p1'))}
          {p(t('sections.recipients.p2'))}
          {recipient('01', true)}
          {recipient('02', false)}
          {recipient('03', false)}
          {p(t('sections.recipients.p3'))}
          {p(t('sections.recipients.p4'))}
        </Box>

        <Box component="section">
          {h2(t('sections.security.title'))}
          {p(t('sections.security.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.rights.title'))}
          {p(t('sections.rights.p1'))}
          {p(t('sections.rights.p2'))}
          {p(t('sections.rights.p3'))}
        </Box>

        <Box component="section">
          {h2(t('sections.externalLinks.title'))}
          {p(t('sections.externalLinks.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.brazil.title'))}
          {p(t('sections.brazil.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.changes.title'))}
          {p(t('sections.changes.p1'))}
        </Box>

        <Box component="section">
          {h2(t('sections.contact.title'))}
          {p(t('sections.contact.p1'))}
          {addressBlock([
            t('sections.contact.company'),
            t('sections.contact.street'),
            t('sections.contact.city'),
            t('sections.contact.email')
          ])}
        </Box>
      </Box>
    </Box>
  )
}
